import BufferedGraph from 'src/components/BufferedGraph';
import StatsItem from 'src/components/Stats/StatsItem';
import useStats from 'src/hooks/useStats';
import { useState, useEffect, useRef, useCallback } from 'react';
import useTranslation from 'next-translate/useTranslation';
import copy from 'copy-to-clipboard';
import { IStats } from 'src/interfaces/Stats';
import {
  CHUNK_STATS,
  MEDIA_STATS,
  AUDIO_STATS,
  GENERAL_STATS,
  BUFFER_STATS,
  EVENT_STATS,
} from 'src/interfaces/StatsEnums';
import classNames from 'classnames';
import useClickOutside, { ClickOutsideEvent } from 'src/hooks/useClickOutside';
import usePlayerData from 'src/hooks/usePlayerData';
import { MEDIA_TYPE } from 'src/interfaces/enums';
import { CheckCircleIcon, CloseIcon, CopyIcon } from '../../icons';
import FloatingOverlay from '../../components/FloatingOverlay';
import IconButton from '../../components/IconButton';
import styles from './styles.module.scss';
import { TIME_INTERVAL } from './constants';

export interface IModalVideoData {
  onHide: () => void;
}

const ModalVideoData = ({ onHide }: IModalVideoData) => {
  const { t } = useTranslation('common');

  const { stats, events } = useStats();
  const { playerData } = usePlayerData();

  const [copied, setCopied] = useState(false);
  const [isPreparedToHide, setIsPreparedToHide] = useState(false);
  const [, setIsReady] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsReady(!!modalRef.current);
  }, []);

  useEffect(() => {
    if (!isPreparedToHide) {
      return;
    }

    const timeout = setTimeout(onHide, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [isPreparedToHide, onHide]);

  const handleClickOutside = useCallback((event: ClickOutsideEvent) => {
    event.stopPropagation();

    setIsPreparedToHide(true);
  }, []);

  useClickOutside(modalRef?.current, handleClickOutside);

  useEffect(() => {
    if (contentRef.current?.parentElement) {
      contentRef.current.parentElement.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timeout = setTimeout(() => {
      setCopied(false);
    }, TIME_INTERVAL);

    return () => {
      clearTimeout(timeout);
    };
  }, [copied]);

  const copyStats = (stats: IStats) => {
    copy(JSON.stringify(stats));
    setCopied(true);
  };

  const mediaStats = playerData.audio ? AUDIO_STATS : MEDIA_STATS;
  const mediaType = playerData.audio ? MEDIA_TYPE.audio : MEDIA_TYPE.video;

  return (
    <FloatingOverlay
      ref={modalRef}
      className={classNames(styles.modal, { [styles.hidden]: isPreparedToHide })}
      title={t(`context_menu.${mediaType}_data`)}
      rightIcon={<IconButton onClick={() => setIsPreparedToHide(true)} icon={<CloseIcon />} />}
    >
      <div ref={contentRef} className={styles.content}>
        <StatsItem statsContent={mediaStats} statsValue={stats.media} title='Media' />
        <StatsItem statsContent={GENERAL_STATS} statsValue={stats.general} title='General' />
        <StatsItem statsContent={BUFFER_STATS} statsValue={stats.buffer} title='Buffer'>
          <BufferedGraph
            bufferStart={stats.buffer.bufferStart}
            bufferEnd={stats.buffer.bufferEnd}
            currentTime={stats.buffer.currentTime}
            duration={stats.general.duration}
          />
        </StatsItem>
        <StatsItem statsContent={CHUNK_STATS} statsValue={stats.chunks} title='Chunks' />
        <StatsItem statsContent={EVENT_STATS} statsValue={events} title='Events' />
        <IconButton
          className={classNames(styles.copyButton, { [styles.copied]: copied })}
          onClick={() => copyStats(stats)}
          icon={
            copied ? (
              <>
                <CheckCircleIcon />
                {t(`stats.${mediaType}.copied`)}
              </>
            ) : (
              <>
                <CopyIcon />
                {t(`stats.${mediaType}.copy`)}
              </>
            )
          }
        />
      </div>
    </FloatingOverlay>
  );
};

export default ModalVideoData;
